/*
 * Above 9 · Tranquitone · the "Find your set" quiz (original work, theme v0.1, 2026-10-04).
 *
 * An optional intro with a Start button, then one question at a time with Back and Next (the last question's
 * button may carry its own text). Every answer carries weighted result keys ("four:2,three:1"). The result with
 * the highest score wins; a tie goes to the result block that comes first; the first result block is the default.
 * A result block may also count other keys, but only while no result block on the page carries that key itself
 * (with The Three off the menu, its points go to The Four). The result shows on the page, never behind an email,
 * and a polite live region announces it. The optional sign-up under the result posts Shopify's customer form with
 * the tags "quiz" and "quiz-<result key>"; after the page reloads from that post, the same result comes back with
 * the form's message. No score, percentage or match rating is ever shown: the tally never leaves this script.
 * A result marked data-a9-cap-off hides the sign-up while it shows (catalog additions, 2026-10-05: the TWO result,
 * until its plan email exists).
 */

const STORAGE_PREFIX = 'a9-quiz:';

/** @param {string | undefined} value */
function parseKeys(value) {
  /** @type {Array<[string, number]>} */
  const pairs = [];
  for (const part of (value || '').split(',')) {
    const [name, weight] = part.split(':');
    if (!name) continue;
    const points = Number(weight);
    pairs.push([name, Number.isFinite(points) && points >= 1 ? points : 1]);
  }
  return pairs;
}

class A9Quiz extends HTMLElement {
  connectedCallback() {
    this.intro = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-intro]'));
    this.startButton = this.querySelector('[data-a9-start]');
    this.form = /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-a9-form]'));
    this.steps = /** @type {HTMLFieldSetElement[]} */ (Array.from(this.querySelectorAll('[data-a9-step]')));
    this.results = /** @type {HTMLElement[]} */ (Array.from(this.querySelectorAll('[data-a9-result]')));
    this.resultsWrap = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-results]'));
    this.nextButton = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-next]'));
    this.backButton = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-back]'));
    this.restartButton = this.querySelector('[data-a9-restart]');
    this.nopick = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-nopick]'));
    this.live = /** @type {HTMLElement | null} */ (this.querySelector('[data-a9-live]'));
    this.capture = /** @type {HTMLFormElement | null} */ (this.querySelector('form[data-a9-capture]'));
    this.tags = /** @type {HTMLInputElement | null} */ (this.capture?.querySelector('[data-a9-tags]') ?? null);
    this.storageKey = STORAGE_PREFIX + (this.dataset.sectionId || '');
    this.index = 0;
    /** @type {HTMLElement | null} */
    this.shown = null;

    if (!this.form || !this.resultsWrap || this.steps.length === 0 || this.results.length === 0) return;

    this.onChange = () => {
      if (this.nopick) this.nopick.hidden = true;
      this.sync();
    };
    this.onSubmit = (/** @type {Event} */ event) => {
      event.preventDefault();
      this.forward();
    };
    this.onStart = () => this.go(0, true);
    this.onBack = () => this.go(this.index - 1, true);
    this.onRestart = () => this.reset(true);
    this.onCapture = () => this.remember();

    this.form.addEventListener('change', this.onChange);
    this.form.addEventListener('submit', this.onSubmit);
    this.startButton?.addEventListener('click', this.onStart);
    this.backButton?.addEventListener('click', this.onBack);
    this.restartButton?.addEventListener('click', this.onRestart);
    this.capture?.addEventListener('submit', this.onCapture);

    this.hidden = false;
    if (this.restore()) return;
    if (this.startButton) this.showIntro();
    else this.go(0, false);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('change', this.onChange);
    this.form?.removeEventListener('submit', this.onSubmit);
    this.startButton?.removeEventListener('click', this.onStart);
    this.backButton?.removeEventListener('click', this.onBack);
    this.restartButton?.removeEventListener('click', this.onRestart);
    this.capture?.removeEventListener('submit', this.onCapture);
  }

  /** @param {HTMLElement | undefined} step */
  picked(step) {
    return /** @type {HTMLInputElement | null} */ (step?.querySelector('input[type="radio"]:checked') ?? null);
  }

  sync() {
    const ready = Boolean(this.picked(this.steps[this.index]));
    this.nextButton?.setAttribute('aria-disabled', ready ? 'false' : 'true');
    if (this.nextButton) {
      const last = this.index === this.steps.length - 1;
      const label = last ? this.nextButton.dataset.labelSee : this.nextButton.dataset.labelNext;
      if (label) this.nextButton.textContent = label;
    }
  }

  showIntro() {
    if (this.intro) this.intro.hidden = false;
    if (this.form) this.form.hidden = true;
    if (this.resultsWrap) this.resultsWrap.hidden = true;
  }

  /**
   * @param {number} index
   * @param {boolean} focus
   */
  go(index, focus) {
    this.index = Math.max(0, Math.min(index, this.steps.length - 1));
    this.steps.forEach((step, i) => {
      step.hidden = i !== this.index;
    });
    if (this.backButton) this.backButton.hidden = this.index === 0;
    if (this.nopick) this.nopick.hidden = true;
    if (this.intro) this.intro.hidden = true;
    if (this.form) this.form.hidden = false;
    if (this.resultsWrap) this.resultsWrap.hidden = true;
    this.sync();
    if (focus) /** @type {HTMLElement | null} */ (this.steps[this.index]?.querySelector('legend'))?.focus();
  }

  forward() {
    const step = this.steps[this.index];
    if (!this.picked(step)) {
      if (this.nopick) this.nopick.hidden = false;
      /** @type {HTMLElement | null} */ (step?.querySelector('input[type="radio"]') ?? null)?.focus();
      return;
    }
    if (this.index < this.steps.length - 1) {
      this.go(this.index + 1, true);
    } else {
      this.show(this.winner(), true);
    }
  }

  winner() {
    /** @type {Map<string, number>} */
    const points = new Map();
    for (const step of this.steps) {
      const choice = this.picked(step);
      if (!choice) continue;
      for (const [key, weight] of parseKeys(choice.dataset.a9Keys)) {
        points.set(key, (points.get(key) || 0) + weight);
      }
    }
    const present = new Set(this.results.map((result) => result.dataset.a9Result || ''));
    let best = this.results[0];
    let top = 0;
    for (const result of this.results) {
      let score = points.get(result.dataset.a9Result || '') || 0;
      for (const alias of (result.dataset.a9Also || '').split(',')) {
        if (alias && !present.has(alias)) score += points.get(alias) || 0;
      }
      if (score > top) {
        best = result;
        top = score;
      }
    }
    return best;
  }

  /**
   * @param {HTMLElement | undefined} result
   * @param {boolean} focus
   */
  show(result, focus) {
    if (!result) return;
    const key = result.dataset.a9Result || '';
    if (this.intro) this.intro.hidden = true;
    if (this.form) this.form.hidden = true;
    this.results.forEach((item) => {
      item.hidden = item !== result;
    });
    if (this.resultsWrap) this.resultsWrap.hidden = false;
    if (this.tags) this.tags.value = `quiz, quiz-${key}`;
    if (this.capture) {
      this.capture.hidden = result.hasAttribute('data-a9-cap-off');
      const bodies = /** @type {HTMLElement[]} */ (Array.from(this.capture.querySelectorAll('[data-a9-cap-body]')));
      const own = bodies.find((body) => body.dataset.a9CapBody === key);
      for (const body of bodies) {
        body.hidden = own ? body !== own : body.dataset.a9CapBody !== '';
      }
    }
    this.shown = result;
    if (this.live?.dataset.template) {
      this.live.textContent = this.live.dataset.template.replace('[set]', result.dataset.a9Name || '');
    }
    if (focus) result.focus();
  }

  /** @param {boolean} focus */
  reset(focus) {
    this.form?.reset();
    this.shown = null;
    if (this.tags) this.tags.value = 'quiz';
    if (this.live) this.live.textContent = '';
    this.capture?.querySelectorAll('[data-a9-posted]').forEach((message) => {
      /** @type {HTMLElement} */ (message).hidden = true;
    });
    const row = /** @type {HTMLElement | null} */ (this.capture?.querySelector('.a9-quiz__capture-row') ?? null);
    if (row) row.hidden = false;
    this.go(0, focus);
  }

  remember() {
    if (!this.shown) return;
    try {
      sessionStorage.setItem(this.storageKey, this.shown.dataset.a9Result || '');
    } catch (error) {
      /* storage blocked: the page still works, only the result is not restored after the reload */
    }
  }

  /** @returns {boolean} */
  restore() {
    let saved = null;
    try {
      saved = sessionStorage.getItem(this.storageKey);
      sessionStorage.removeItem(this.storageKey);
    } catch (error) {
      return false;
    }
    if (saved === null) return false;
    const message = /** @type {HTMLElement | null} */ (this.capture?.querySelector('[data-a9-posted]') ?? null);
    if (!message) return false;
    const result = this.results.find((item) => item.dataset.a9Result === saved) || this.results[0];
    this.show(result, false);
    message.hidden = false;
    if (message.dataset.a9Posted === 'success') {
      const row = /** @type {HTMLElement | null} */ (this.capture?.querySelector('.a9-quiz__capture-row') ?? null);
      if (row) row.hidden = true;
    }
    message.focus();
    return true;
  }
}

if (!customElements.get('a9-quiz')) customElements.define('a9-quiz', A9Quiz);
